import { useEffect, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { FriendLocation, TemporaryLocationWithFriend, LocationType } from '../../../types';
import { createFriendIcon, getTempLocationIcon } from '../utils';
import { useRoute } from '../../../contexts/RouteContext';

// Location types configuration
const LOCATION_TYPES = [
  { value: 'vacation' as LocationType, label: 'Vacances', icon: '🏖️', color: '#FF6B6B' },
  { value: 'party' as LocationType, label: 'Soirée', icon: '🎉', color: '#4ECDC4' },
  { value: 'work' as LocationType, label: 'Travail', icon: '💼', color: '#45B7D1' },
  { value: 'other' as LocationType, label: 'Autre', icon: '📍', color: '#95A5A6' },
];

interface FriendMarkerProps {
  friend: FriendLocation;
  shouldOpenPopup: boolean;
  onContactClick: (id: number) => void;
  onDirectionsClick: (info: {
    lat: number;
    lng: number;
    name?: string;
    address?: string;
    profilePhoto?: string;
    firstName?: string;
    lastName?: string;
    friendId?: number;
  }) => void;
  getPhotoUrl: (photo?: string) => string | null;
  loadingFriend: boolean;
  t: (key: string) => string;
  tempLocations?: TemporaryLocationWithFriend[];
  onTempLocationClick?: (tempLocation: TemporaryLocationWithFriend) => void;
  zIndexOffset?: number; // Higher value = marker appears on top of others
}

/**
 * Component for friend marker with popup control
 * Displays a friend's location on the map with their photo and actions
 */
export const FriendMarker: React.FC<FriendMarkerProps> = ({
  friend,
  shouldOpenPopup,
  onContactClick,
  onDirectionsClick,
  getPhotoUrl,
  loadingFriend,
  t,
  tempLocations = [],
  onTempLocationClick,
  zIndexOffset = 0,
}) => {
  const markerRef = useRef<any>(null);
  const { isRoutingMode, addWaypoint, origin, destination, waypoints } = useRoute();

  useEffect(() => {
    if (shouldOpenPopup && markerRef.current) {
      markerRef.current.openPopup();
    }
  }, [shouldOpenPopup]);

  const photoUrl = getPhotoUrl(friend.profile_photo);

  // Check if this is my own location
  const isMyLocation = (friend as any).isMyLocation === true || friend.markerType === 'myTemporary';

  // Check if this friend is part of the current route (origin, destination, or waypoint)
  // If so, RoutePolyline will render the marker - we return null to avoid duplicates
  const friendIdToCheck = friend.friendId || friend.id;

  const isRoutePoint = (() => {
    // Check if origin
    if (origin?.friendId != null && origin.friendId === friendIdToCheck) return true;

    // Check if destination
    if (destination?.friendId != null && destination.friendId === friendIdToCheck) return true;

    // Check if waypoint
    if (waypoints.some(wp => wp.friendId != null && wp.friendId === friendIdToCheck)) return true;

    return false;
  })();

  // If this friend is a route point, don't render - RoutePolyline handles it
  if (isRoutePoint) {
    return null;
  }

  // Use appropriate icon based on marker type
  const friendIcon = (friend.markerType === 'temporary' || friend.markerType === 'myTemporary') && friend.tempLocationType
    ? getTempLocationIcon(friend.tempLocationType, photoUrl, friend.first_name, friend.last_name, LOCATION_TYPES)
    : createFriendIcon(photoUrl, friend.first_name, friend.last_name, false, false);

  // Check if friend has an active temp location
  const activeTempLocation = tempLocations.find(
    (tempLoc) => tempLoc.friend_id === friend.id && tempLoc.is_active
  );

  // Use display coordinates (with jitter) if available, otherwise use original coordinates
  const displayLat = friend.displayLatitude ?? friend.latitude!;
  const displayLng = friend.displayLongitude ?? friend.longitude!;

  return (
    <Marker
      ref={markerRef}
      position={[displayLat, displayLng]}
      icon={friendIcon}
      zIndexOffset={zIndexOffset}
    >
      <Popup>
        <div className="map-popup">
          {friend.profile_photo && (
            <img
              src={photoUrl || undefined}
              alt={`${friend.first_name} ${friend.last_name}`}
              className="popup-photo"
            />
          )}
          <h3>
            {friend.markerType === 'myPermanent' ? '🏠 My permanent address' : `${friend.first_name} ${friend.last_name}`}
          </h3>

          {/* If this is a temporary location marker, show temp location details */}
          {(friend.markerType === 'temporary' || friend.markerType === 'myTemporary') && friend.tempLocationName ? (
            <>
              <p style={{ margin: 0, marginBottom: '8px' }}>
                {/* Only show emoji if not "other" type */}
                {friend.tempLocationType !== 'other' && (LOCATION_TYPES.find(lt => lt.value === friend.tempLocationType)?.icon || '')}
                {friend.tempLocationType !== 'other' && ' '}
                {friend.tempLocationName}
              </p>

              {/* Only show type badge if not "other" */}
              {friend.tempLocationType !== 'other' && (
                <span className="temp-location-type-badge" style={{
                  backgroundColor: LOCATION_TYPES.find(lt => lt.value === friend.tempLocationType)?.color || '#95A5A6'
                }}>
                  {LOCATION_TYPES.find(lt => lt.value === friend.tempLocationType)?.label || ''}
                </span>
              )}
              {friend.tempLocationDescription && (
                <p style={{ marginTop: '8px', fontSize: '13px' }}>{friend.tempLocationDescription}</p>
              )}
              {friend.tempLocationStartDate && friend.tempLocationEndDate && (
                <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                  {(() => {
                    const startDate = new Date(friend.tempLocationStartDate);
                    const endDate = new Date(friend.tempLocationEndDate);
                    const sameDay = startDate.toDateString() === endDate.toDateString();
                    const type = friend.tempLocationType;

                    const formatDate = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
                    const formatTime = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h');

                    if (type === 'party') {
                      // Soirée: just the date, with hours if same day
                      const dayStr = formatDate(startDate);
                      if (sameDay) {
                        return <>{dayStr}<br />de {formatTime(startDate)} à {formatTime(endDate)}</>;
                      }
                      return `Le ${dayStr}`;
                    } else if (type === 'work') {
                      // Travail: period
                      return <>Du {formatDate(startDate)}<br />au {formatDate(endDate)}</>;
                    } else if (type === 'vacation') {
                      // Vacances: until date
                      return `Jusqu'au ${formatDate(endDate)}`;
                    } else {
                      // Other: smart format based on same day or not
                      if (sameDay) {
                        return <>{formatDate(startDate)}<br />de {formatTime(startDate)} à {formatTime(endDate)}</>;
                      } else {
                        return <>Du {formatDate(startDate)}<br />au {formatDate(endDate)}</>;
                      }
                    }
                  })()}
                </p>
              )}
            </>
          ) : (
            <>
              <p>
                {friend.city}, {friend.country}
              </p>

              {/* Active temp location indicator (only for official addresses) */}
              {activeTempLocation && (
            <div className="friend-traveling-indicator">
              <div className="traveling-title">
                ✈️ En déplacement
              </div>
              <div className="traveling-location">
                {activeTempLocation.name}
              </div>
              {onTempLocationClick && (
                <button
                  onClick={() => {
                    // Close popup before navigating
                    if (markerRef.current) {
                      markerRef.current.closePopup();
                    }
                    onTempLocationClick(activeTempLocation);
                  }}
                  style={{
                    marginTop: '6px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer'
                  }}
                >
                  📍 Voir la position
                </button>
              )}
            </div>
              )}
            </>
          )}

          <div className="popup-buttons">
            {/* Don't show contact button for my own locations */}
            {!isMyLocation && (
              <button
                className="popup-icon-button contact-icon"
                onClick={() => onContactClick(friend.friendId || friend.id)}
                disabled={loadingFriend}
                title={t('map.popup.contact')}
              >
                📞
              </button>
            )}
            {friend.latitude && friend.longitude && (
              <button
                className="popup-icon-button directions-icon"
                onClick={() => onDirectionsClick({
                  lat: friend.latitude!,
                  lng: friend.longitude!,
                  name: `${friend.first_name} ${friend.last_name}`,
                  address: friend.city ? `${friend.city}, ${friend.country}` : undefined,
                  profilePhoto: friend.profile_photo,
                  firstName: friend.first_name,
                  lastName: friend.last_name,
                  friendId: friend.friendId || friend.id,
                })}
                title="Itinéraire"
              >
                <img
                  src="/itinéraire-icon-blanc.png"
                  alt="Itinéraire"
                  style={{
                    width: '20px',
                    height: '20px'
                  }}
                />
              </button>
            )}
            {/* Add as waypoint button - only in routing mode with origin and destination set */}
            {isRoutingMode && origin && destination && friend.latitude && friend.longitude && (
              <button
                className="popup-icon-button waypoint-icon"
                onClick={() => {
                  addWaypoint({
                    lat: friend.latitude!,
                    lng: friend.longitude!,
                    name: `${friend.first_name} ${friend.last_name}`,
                    address: friend.city ? `${friend.city}, ${friend.country}` : undefined,
                    // Include marker info for proper route rendering
                    markerType: 'friend',
                    friendId: friend.id,
                    firstName: friend.first_name,
                    lastName: friend.last_name,
                    profilePhoto: friend.profile_photo,
                  });
                  // Close popup after adding
                  if (markerRef.current) {
                    markerRef.current.closePopup();
                  }
                }}
                title={t('routing.addWaypoint')}
              >
                +
              </button>
            )}
          </div>
        </div>
      </Popup>
    </Marker>
  );
};
